import { useCallback, useEffect, useState } from "react";
import type { ApiKey, CreateApiKeyPayload } from "api";
import { apiKeysApi } from "api";
import { getErrorMessage, isForbidden, isNotFound, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

export type CreatedApiKey = {
  id: number;
  name: string;
  token: string;
};

/** 403 here means the account exists but has not confirmed its email address. */
export const UNVERIFIED_MESSAGE = "Verify your email address to manage API keys.";

type UseApiKeys = {
  keys: ApiKey[];
  status: LoadStatus;
  /** Plain value of the key just created — the only time it is ever available. */
  createdKey: CreatedApiKey | null;
  creating: boolean;
  createError: string | null;
  revokingId: number | null;
  revokeError: string | null;
  create: (payload: CreateApiKeyPayload) => Promise<boolean>;
  revoke: (id: number) => Promise<void>;
  dismissCreatedKey: () => void;
};

export const useApiKeys = (onSessionLost: () => void): UseApiKeys => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    apiKeysApi
      .list()
      .then(({ data }) => {
        if (cancelled) return;
        setKeys(data.data);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthenticated(error)) {
          onSessionLost();
          setStatus("unauthorized");
          return;
        }
        setStatus(isForbidden(error) ? "unverified" : "failed");
      });

    return () => {
      cancelled = true;
    };
  }, [onSessionLost]);

  const create = useCallback(
    async (payload: CreateApiKeyPayload): Promise<boolean> => {
      setCreating(true);
      setCreateError(null);

      try {
        const { data } = await apiKeysApi.create(payload);

        setKeys((current) => [data.data, ...current]);
        setCreatedKey({ id: data.data.id, name: data.data.name, token: data.token });

        return true;
      } catch (error) {
        if (isUnauthenticated(error)) {
          onSessionLost();
        } else if (isForbidden(error)) {
          setCreateError(UNVERIFIED_MESSAGE);
        } else {
          setCreateError(getErrorMessage(error, "Could not create the key. Please try again."));
        }

        return false;
      } finally {
        setCreating(false);
      }
    },
    [onSessionLost]
  );

  const revoke = useCallback(
    async (id: number) => {
      setRevokingId(id);
      setRevokeError(null);

      try {
        await apiKeysApi.revoke(id);
        setKeys((current) => current.filter((key) => key.id !== id));
      } catch (error) {
        if (isUnauthenticated(error)) {
          onSessionLost();
        } else if (isNotFound(error)) {
          // Already gone, so the user got what they asked for; just drop the row.
          setKeys((current) => current.filter((key) => key.id !== id));
        } else if (isForbidden(error)) {
          setRevokeError(UNVERIFIED_MESSAGE);
        } else {
          setRevokeError(getErrorMessage(error, "Could not revoke the key. Please try again."));
        }
      } finally {
        setRevokingId(null);
      }
    },
    [onSessionLost]
  );

  const dismissCreatedKey = useCallback(() => setCreatedKey(null), []);

  return {
    keys,
    status,
    createdKey,
    creating,
    createError,
    revokingId,
    revokeError,
    create,
    revoke,
    dismissCreatedKey,
  };
};
